import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GridState, GridStateCompositeFilterDescriptor, MessageService } from '@shared';
import {
  Alerts,
  AlertsService,
  GlobalEventsService,
  ItemService,
} from '@shared-data';
import { TokenService } from '@auth';
import { CategoryConstants } from '../../constants';

@Component({
  selector: 'app-alerts-banner',
  templateUrl: './alerts-banner.component.html',
  styleUrl: './alerts-banner.component.scss',
})
export class AlertsBannerComponent implements OnInit {
  @ViewChild('alertsAnchor', { read: ElementRef }) alertsAnchor!: ElementRef;

  alerts?: Alerts;

  alertsPopupVisible = false;

  alertsDropdownVisible = false;

  syncStatusDropdownVisible = false;

  acknowledgementVisible: boolean = false;

  syncAcknowledgementVisible: boolean = false;

  newLtos: { itemName: string; parItemId: string; documentPath: string[] }[] = [];

  isAvailabilityOnly: boolean = false;

  get failedSyncCount(): number {
    return this.alerts?.failedSyncCount ?? 0;
  }

  get alertCount(): number {
    let count = 0;
    if (this.alerts) {
      if (this.alerts.newLtoCount > 0 && !this.isAvailabilityOnly) count++;
      if (this.alerts.zeroPriceCount > 0) count++;
    }
    return count;
  }

  constructor(
    private alertService: AlertsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private globalEventsService: GlobalEventsService,
    private messageService: MessageService,
    private tokenService: TokenService,
    private itemService: ItemService,
  ) {}

  async ngOnInit() {
    this.isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();
    this.getAlerts();
    this.subscribeToNavigateAwayFromLTO();
    this.subscribeToNavigateAwayFromSyncHistory();
  }

  // openPopup is off when the counts are refreshed after an acknowledgement: the user is in the
  // middle of navigating away, and the remaining alerts would otherwise pop the login dialog
  // open again in the middle of it.
  getAlerts(openPopup: boolean = true) {
    this.alertService.getAlerts().subscribe({
      next: (alerts) => {
        this.alerts = alerts;

        const hasLto = alerts.newLtoCount > 0 && !this.isAvailabilityOnly;
        const hasZeroPrice = alerts.zeroPriceCount > 0;
        const hasFailedSyncs = alerts.failedSyncCount > 0;

        if (openPopup && (hasLto || hasZeroPrice || hasFailedSyncs)) {
          this.alertsPopupVisible = true;
        }

        if (hasLto) {
          const filter = new GridStateCompositeFilterDescriptor().and({
            field: 'categoryName',
            operator: 'eq',
            value: CategoryConstants.LTO,
          });
          const state = new GridState(1000);
          state.filter = filter;
          this.itemService.getNewPreview(state).subscribe({
            next: (preview) => {
              this.newLtos = preview.data;
            },
            error: () => {
              this.messageService.error('Error fetching new LTO preview');
            },
          });
        }
      },
    });
  }

  // --- Dropdown navigation ---

  // With no failures to review the dropdown would hold a single read-only line, so the chip keeps
  // behaving the way it always has and goes straight to the History page.
  onSyncStatusClick() {
    if (this.failedSyncCount > 0) {
      this.syncStatusDropdownVisible = !this.syncStatusDropdownVisible;
      return;
    }
    this.navigateToReport();
  }

  navigateToReport() {
    this.router.navigate(['/reports/history-summary'], {
      relativeTo: this.activatedRoute,
    });
  }

  // The failures land on the same grid the History tab opens: /reports/history stopped being a
  // grid of its own and is now the detail of a single sync, reached by clicking one of the counts
  // (F25MBO11A-814), so sending the user there without a sync to look at shows nothing. The
  // summary grid has no failures-only view to link to either: the Failed column is what the user
  // reads, and clicking a number in it opens the detail.
  navigateToFailedSyncs() {
    this.navigateToReport();
  }

  navigateToLtos() {
    this.router.navigate(['/restaurantitem/pricing/lto'], {
      relativeTo: this.activatedRoute,
      queryParams: { isLto: true },
    });
  }

  navigateToZeroPriceReport() {
    this.router.navigate(['/reports/exceptions/pricing/zero-price'], {
      relativeTo: this.activatedRoute,
    });
  }

  // --- Popup card actions ---

  // The cards no longer offer a Hide button: it only closed the card for the session and gave
  // the impression the alert had been dealt with, so the popup is now dismissed as a whole and
  // each alert is silenced from the screen where it is actually reviewed.
  viewLtoAlert() {
    this.alertsPopupVisible = false;
    this.navigateToLtos();
  }

  viewZeroPriceAlert() {
    this.alertsPopupVisible = false;
    this.navigateToZeroPriceReport();
  }

  viewFailedSyncAlert() {
    this.alertsPopupVisible = false;
    this.navigateToFailedSyncs();
  }

  // --- Acknowledgement ---

  acknowledgeLtos() {
    this.alertService.acknowledgeLtoAlert().subscribe({
      next: () => {
        this.getAlerts();
      },
      error: () => {
        this.messageService.error('Error acknowledging LTO alert');
      },
    });
  }

  downloadDocument(item: { itemName: string; parItemId: string }, documentPath: string) {
    this.itemService.downloadDocument(documentPath).subscribe({
      next: (response: { url: string }) => {
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = response.url;
        link.download = `${item.itemName}_${item.parItemId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      error: () => {
        this.messageService.error('Error downloading document');
      },
    });
  }

  acknowledgeSyncFailures() {
    this.syncAcknowledgementVisible = false;
    this.alertService.acknowledgeSyncFailureAlert().subscribe({
      next: () => {
        this.getAlerts(false);
      },
      error: () => {
        this.messageService.error('Error acknowledging sync failure alert');
      },
    });
  }

  subscribeToNavigateAwayFromLTO() {
    this.globalEventsService.navigatedAwayFromLTO$.subscribe(() => {
      this.acknowledgementVisible = true;
    });
  }

  // Only ask when there was something to review: the Sync History page is also reached from the
  // History tab by users who have no failures at all, and they should not be prompted.
  subscribeToNavigateAwayFromSyncHistory() {
    this.globalEventsService.navigatedAwayFromSyncHistory$.subscribe(() => {
      if (this.failedSyncCount > 0) {
        this.syncAcknowledgementVisible = true;
      }
    });
  }

}
